import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import canteenService from '../services/canteenService';
import securityService from '../services/securityService';
import { useAuth } from '../context/AuthContext';
import DialogModal from '../components/DialogModal';
import { Html5Qrcode } from 'html5-qrcode';
import BarcodeScanner from '../components/BarcodeScanner';
import {
  QrCode, PencilSimple, Plus, Trash, ArrowClockwise, Scan, Keyboard, X,
  CheckCircle, WarningCircle, IdentificationCard, Minus, UserCircle, ArrowLeft,
} from '@phosphor-icons/react';

const QR_READER_ELEMENT_ID = 'canteen-pickup-qr-reader';
const CATEGORIES = ['breakfast', 'lunch', 'snacks', 'drinks', 'stationery'];

function formatCurrency(amount) {
  return `GHS ${Number(amount || 0).toFixed(2)}`;
}

const STATUS_COLUMNS = [
  { key: 'pending', label: 'New' },
  { key: 'kitchen', label: 'Preparing', statuses: ['accepted', 'preparing'] },
  { key: 'ready', label: 'Ready' },
  { key: 'done', label: 'Done', statuses: ['completed', 'rejected', 'cancelled'] },
];

function titleCase(s) {
  return (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const EMPTY_MENU_FORM = {
  name: '', type: 'lunch', price: '', info: '', stock: '', needsPrep: false,
  calories: '', proteinG: '', carbsG: '', fatG: '', allergens: '', supplierId: '',
};

function MenuTab() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState(EMPTY_MENU_FORM);
  const [loading, setLoading] = useState(true);
  const [selectedQrItem, setSelectedQrItem] = useState(null);

  const loadItems = async () => {
    try {
      const data = await canteenService.listItems();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || 'Unable to load canteen items');
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = async () => {
    try {
      const data = await canteenService.getVendors();
      setVendors(Array.isArray(data) ? data : []);
    } catch (error) {
      // Non-fatal — the supplier picker just stays empty if this fails.
      console.error('Error fetching canteen vendors:', error);
    }
  };

  useEffect(() => { loadItems(); loadVendors(); }, []);

  const vendorName = (id) => vendors.find(v => v.id === id)?.name;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) {
      toast.error('Please complete the item name and price');
      return;
    }

    try {
      const newItem = await canteenService.createItem({
        name: form.name,
        item_type: form.type,
        price: Number(form.price),
        info: form.info || undefined,
        stock_count: form.stock === '' ? null : Number(form.stock),
        needs_prep: form.needsPrep,
        calories: form.calories === '' ? undefined : Number(form.calories),
        protein_g: form.proteinG === '' ? undefined : Number(form.proteinG),
        carbs_g: form.carbsG === '' ? undefined : Number(form.carbsG),
        fat_g: form.fatG === '' ? undefined : Number(form.fatG),
        allergens: form.allergens || undefined,
        supplier_id: form.supplierId || undefined,
      });

      setItems(prev => [newItem, ...prev]);
      setForm(EMPTY_MENU_FORM);
      toast.success('Menu item created');
    } catch (error) {
      toast.error(error.message || 'Unable to create canteen item');
    }
  };

  const removeItem = async (id) => {
    try {
      await canteenService.deleteItem(id);
      setItems(prev => prev.filter(item => item.id !== id));
      toast.success('Item removed');
    } catch (error) {
      toast.error(error.message || 'Unable to remove item');
    }
  };

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <div style={{ display: 'grid', gap: 24, gridTemplateColumns: '1.1fr 0.9fr' }}>
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ marginBottom: 12 }}>Add Menu Item</h3>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
            <input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Item name" />
            <select className="form-control" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{titleCase(cat)}</option>
              ))}
            </select>
            <input className="form-control" type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price (GHS)" />
            <input className="form-control" type="number" min="0" step="1" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock count (leave blank for unlimited)" />
            <textarea className="form-control" value={form.info} onChange={(e) => setForm({ ...form, info: e.target.value })} placeholder="Item details (optional)" rows={3} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.needsPrep}
                onChange={(e) => setForm({ ...form, needsPrep: e.target.checked })}
              />
              Made to order (needs the kitchen to prepare it — goes through Accept → Preparing → Ready). Leave unchecked for grab-and-go items, which are ready for pickup immediately.
            </label>

            <select
              className="form-control"
              value={form.supplierId}
              onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
            >
              <option value="">No supplier / vendor</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>

            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Nutrition (optional)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <input className="form-control" type="number" min="0" step="1" value={form.calories} onChange={(e) => setForm({ ...form, calories: e.target.value })} placeholder="Calories" />
                <input className="form-control" type="number" min="0" step="0.1" value={form.proteinG} onChange={(e) => setForm({ ...form, proteinG: e.target.value })} placeholder="Protein (g)" />
                <input className="form-control" type="number" min="0" step="0.1" value={form.carbsG} onChange={(e) => setForm({ ...form, carbsG: e.target.value })} placeholder="Carbs (g)" />
                <input className="form-control" type="number" min="0" step="0.1" value={form.fatG} onChange={(e) => setForm({ ...form, fatG: e.target.value })} placeholder="Fat (g)" />
              </div>
              <input
                className="form-control"
                style={{ marginTop: 8 }}
                value={form.allergens}
                onChange={(e) => setForm({ ...form, allergens: e.target.value })}
                placeholder="Allergens, comma-separated (e.g. peanuts, dairy)"
              />
            </div>

            <button className="btn btn-primary" type="submit"><Plus size={16} /> Add Item</button>
          </form>
        </div>

        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ marginBottom: 12 }}>Menu</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            {loading ? (
              <div style={{ color: '#6b7280', fontSize: 13 }}>Loading items...</div>
            ) : items.length === 0 ? (
              <div style={{ color: '#6b7280', fontSize: 13 }}>No menu items yet.</div>
            ) : items.map(item => (
              <div key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700 }}>
                    {item.name} <span style={{ fontWeight: 400, fontSize: 12, color: '#6b7280', textTransform: 'capitalize' }}>· {item.item_type}</span>
                    {item.needs_prep && <span style={{ marginLeft: 6, fontSize: 11, color: '#7c3aed', background: '#f5f3ff', padding: '1px 6px', borderRadius: 6 }}>Made to order</span>}
                  </div>
                  {item.info && <div style={{ color: '#6b7280', fontSize: 13 }}>{item.info}</div>}
                  <div style={{ marginTop: 6, color: '#2563eb', fontSize: 13 }}>
                    {formatCurrency(item.price)} • {item.code}
                    {item.stock_count !== null && item.stock_count !== undefined && ` • ${item.stock_count} in stock`}
                  </div>
                  {item.supplier_id && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                      Supplier: {vendorName(item.supplier_id) || item.supplier_id}
                    </div>
                  )}
                  {(item.calories || item.protein_g || item.carbs_g || item.fat_g) && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                      {[
                        item.calories != null && `${item.calories} kcal`,
                        item.protein_g != null && `${item.protein_g}g protein`,
                        item.carbs_g != null && `${item.carbs_g}g carbs`,
                        item.fat_g != null && `${item.fat_g}g fat`,
                      ].filter(Boolean).join(' • ')}
                    </div>
                  )}
                  {item.allergens && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>
                      <WarningCircle size={12} /> Allergens: {item.allergens}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" type="button" onClick={() => setSelectedQrItem(item)}><QrCode size={16} /></button>
                  <button className="btn btn-danger" type="button" onClick={() => removeItem(item.id)}><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedQrItem && (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h3 style={{ margin: 0 }}>QR Preview</h3>
              <p style={{ margin: '4px 0 0', color: '#6b7280' }}>{selectedQrItem.name} • {selectedQrItem.code}</p>
            </div>
            <button className="btn btn-secondary" type="button" onClick={() => setSelectedQrItem(null)}>Close</button>
          </div>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(selectedQrItem.code)}`}
              alt={`QR for ${selectedQrItem.name}`}
              style={{ width: 220, height: 220, borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff' }}
            />
            <div>
              <div style={{ fontWeight: 700 }}>{selectedQrItem.name}</div>
              <div style={{ color: '#6b7280', marginTop: 4 }}>{selectedQrItem.info || 'No details provided'}</div>
              <div style={{ marginTop: 8, color: '#2563eb', fontWeight: 600 }}>Price: {formatCurrency(selectedQrItem.price)}</div>
              <div style={{ marginTop: 8, fontSize: 13, color: '#374151' }}>Item code: <strong>{selectedQrItem.code}</strong></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickupModal, setPickupModal] = useState(null); // order being verified
  const [manualCode, setManualCode] = useState('');
  const [scannerMode, setScannerMode] = useState('camera');
  const [scannerError, setScannerError] = useState(null);
  const [scannerActive, setScannerActive] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  const loadOrders = useCallback(() => {
    canteenService.listOrders({ limit: 100 })
      .then((data) => setOrders(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load orders'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 15000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  const handleAdvance = async (order, newStatus) => {
    try {
      await canteenService.updateOrderStatus(order.id, newStatus);
      toast.success(`Order ${order.order_code} → ${titleCase(newStatus)}`);
      loadOrders();
    } catch (error) {
      toast.error(error.message || 'Could not update order');
    }
  };

  const handleReject = (order) => {
    setPromptDialog({
      title: 'Reject Order',
      message: 'Reason for rejecting this order (shown to the student):',
      defaultValue: 'Out of stock',
      onSubmit: async (reason) => {
        try {
          await canteenService.updateOrderStatus(order.id, 'rejected', reason);
          toast.success(`Order ${order.order_code} rejected`);
          loadOrders();
        } catch (error) {
          toast.error(error.message || 'Could not reject order');
        }
      },
    });
  };

  const openPickupModal = (order) => {
    setPickupModal(order);
    setManualCode('');
    setScannerError(null);
    setScannerMode('camera');
    setScannerActive(true);
  };

  const closePickupModal = () => {
    setPickupModal(null);
    setScannerActive(false);
  };

  const submitPickupCode = useCallback(async (code) => {
    if (!pickupModal || !code) return;
    setVerifying(true);
    try {
      await canteenService.verifyPickup(pickupModal.id, code);
      toast.success(`Order ${pickupModal.order_code} completed`);
      setScannerActive(false);
      setPickupModal(null);
      loadOrders();
    } catch (error) {
      toast.error(error.message || 'Pickup code did not match');
    } finally {
      setVerifying(false);
    }
  }, [pickupModal, loadOrders]);

  // QR scanner for pickup codes — mirrors the crash-safe pattern used in
  // the security module (only call stop() once start() has resolved).
  useEffect(() => {
    if (!scannerActive || scannerMode !== 'camera' || !pickupModal) return;

    let isActive = true;
    let started = false;
    const html5Qr = new Html5Qrcode(QR_READER_ELEMENT_ID);

    html5Qr.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 220 },
      (decodedText) => {
        if (!isActive) return;
        isActive = false;
        if (started) html5Qr.stop().catch(() => {});
        submitPickupCode(decodedText.trim());
      },
      () => { /* per-frame scan miss — ignore */ }
    ).then(() => { started = true; }).catch((err) => {
      if (!isActive) return;
      setScannerMode('manual');
      setScannerError('Camera unavailable — enter the code manually.');
      console.warn('Pickup QR camera start failed:', err);
    });

    return () => {
      isActive = false;
      if (started) html5Qr.stop().then(() => html5Qr.clear()).catch(() => {});
    };
  }, [scannerActive, scannerMode, pickupModal, submitPickupCode]);

  const columnOrders = (col) => {
    const statuses = col.statuses || [col.key];
    return orders.filter(o => statuses.includes(o.status));
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={loadOrders}><ArrowClockwise size={14} /> Refresh</button>
      </div>

      {loading ? (
        <div style={{ color: '#6b7280' }}>Loading orders...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {STATUS_COLUMNS.map(col => (
            <div key={col.key}>
              <h4 style={{ marginBottom: 10 }}>{col.label} ({columnOrders(col).length})</h4>
              <div style={{ display: 'grid', gap: 10 }}>
                {columnOrders(col).map(order => (
                  <div key={order.id} className="card" style={{ padding: 12 }}>
                    <div style={{ fontWeight: 700 }}>{order.order_code}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {order.items.map(l => `${l.quantity}x ${l.name}`).join(', ')}
                    </div>
                    {order.note && <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 4 }}>Note: {order.note}</div>}
                    <div style={{ fontWeight: 700, marginTop: 6 }}>{formatCurrency(order.total)}</div>

                    <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {order.status === 'pending' && (
                        <>
                          <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 13 }} onClick={() => handleAdvance(order, 'accepted')}>Accept</button>
                          <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 13 }} onClick={() => handleReject(order)}>Reject</button>
                        </>
                      )}
                      {order.status === 'accepted' && (
                        <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 13 }} onClick={() => handleAdvance(order, 'preparing')}>Start Preparing</button>
                      )}
                      {order.status === 'preparing' && (
                        <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 13 }} onClick={() => handleAdvance(order, 'ready')}>Mark Ready</button>
                      )}
                      {order.status === 'ready' && (
                        <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 13 }} onClick={() => openPickupModal(order)}>
                          <QrCode size={14} /> Verify Pickup
                        </button>
                      )}
                      {['completed', 'rejected', 'cancelled'].includes(order.status) && (
                        <div style={{ fontSize: 12, color: order.status === 'completed' ? '#16a34a' : '#6b7280' }}>
                          {order.status === 'completed' ? <CheckCircle size={12} /> : <X size={12} />} {titleCase(order.status)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {columnOrders(col).length === 0 && (
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>None</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {pickupModal && (
        <div className="sec-modal-overlay" onClick={closePickupModal}>
          <div className="sec-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <p className="sec-modal-title" style={{ margin: 0 }}>Verify Pickup — {pickupModal.order_code}</p>
              <button className="btn btn-secondary" style={{ padding: '4px 8px' }} onClick={closePickupModal}><X size={16} /></button>
            </div>

            {scannerMode === 'camera' ? (
              <>
                <div id={QR_READER_ELEMENT_ID} style={{ width: '100%', minHeight: 220, borderRadius: 12, overflow: 'hidden', background: '#0f172a' }} />
                <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                  Ask the student to show their pickup QR code.
                </div>
                <button className="btn btn-secondary" style={{ marginTop: 8, width: '100%' }} onClick={() => { setScannerActive(false); setScannerMode('manual'); setScannerError(null); }}>
                  <Keyboard size={14} /> Enter code manually
                </button>
              </>
            ) : (
              <>
                {scannerError && (
                  <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>
                    <WarningCircle size={12} /> {scannerError}
                  </div>
                )}
                <input
                  type="text"
                  className="form-control"
                  placeholder="6-digit pickup code"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  maxLength={6}
                />
                <button
                  className="btn btn-primary"
                  style={{ marginTop: 8, width: '100%' }}
                  disabled={verifying || manualCode.trim().length !== 6}
                  onClick={() => submitPickupCode(manualCode.trim())}
                >
                  {verifying ? 'Verifying...' : 'Confirm Pickup'}
                </button>
                <button className="btn btn-secondary" style={{ marginTop: 8, width: '100%' }} onClick={() => { setScannerMode('camera'); setScannerActive(true); setScannerError(null); }}>
                  <Scan size={14} /> Use camera instead
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="warning"
        input={{ placeholder: 'Reason', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

// Counter staff scans a student's existing ID card (the same card_number QR
// used by KioskLoginPage.js) to identify them, then charges their canteen
// wallet directly — a real-time cafeteria-line flow, distinct from the
// self-order-ahead flow the rest of this page fulfills. Uses
// components/BarcodeScanner.js (built for this purpose) rather than a raw
// Html5Qrcode instance like the pickup-verification scanner above.
function ScanChargeTab() {
  const { user } = useAuth();
  const [scanning, setScanning] = useState(true);
  const [student, setStudent] = useState(null); // scan result: identity + wallet snapshot
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState({}); // item_id -> quantity
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    canteenService.listItems()
      .then((data) => setItems(Array.isArray(data) ? data.filter((i) => i.is_active) : []))
      .catch(() => toast.error('Failed to load menu items'));
  }, []);

  const reset = () => {
    setStudent(null);
    setCart({});
    setNote('');
    setLastOrder(null);
    setScanning(true);
  };

  // Shared by both the manual card-scan path and the face-recognition SSE
  // push below — either way, "a student was identified" ends the same way.
  const applyStudent = (data) => {
    setScanning(false);
    setStudent(data);
  };

  const handleScan = async (cardNumber) => {
    setScanning(false);
    try {
      const data = await canteenService.scanCard(cardNumber.trim());
      applyStudent(data);
    } catch (error) {
      toast.error(error.message || 'Card not recognized');
      setScanning(true);
    }
  };

  // Live push: a registered face-recognition terminal (routers/biometric_adms.py)
  // recognizing a student auto-populates the card below with zero staff
  // interaction — the manual card-scan path above stays as the fallback for
  // schools without (or between) terminals. Only acts while genuinely
  // waiting for a scan, so it can't clobber an in-progress charge.
  const waitingForScan = !student && !lastOrder;
  useEffect(() => {
    if (!user?.school_id || !waitingForScan) return undefined;
    let es;
    try {
      es = new EventSource(securityService.getEventsStreamUrl(user.school_id));
    } catch (err) {
      console.warn('SSE not available:', err);
      return undefined;
    }
    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'canteen_face_scan' && data.school_id === user.school_id) {
          applyStudent(data);
        }
      } catch (err) {
        console.warn('Failed parsing SSE event', err);
      }
    };
    es.onerror = (err) => console.warn('SSE error', err);
    return () => { try { es.close(); } catch (_) {} };
  }, [user?.school_id, waitingForScan]);

  const blockedCategories = student?.wallet?.blocked_categories || [];
  const cartTotal = items.reduce((sum, item) => sum + (cart[item.id] || 0) * item.price, 0);
  const cartLines = Object.entries(cart).filter(([, qty]) => qty > 0).map(([item_id, quantity]) => ({ item_id, quantity }));

  const setQty = (itemId, qty) => {
    setCart((prev) => ({ ...prev, [itemId]: Math.max(0, qty) }));
  };

  const charge = async () => {
    if (!cartLines.length || !student) return;
    setSubmitting(true);
    try {
      const result = await canteenService.placeOrder({ items: cartLines, note: note || undefined, studentId: student.student_id });
      setLastOrder(result.order);
      toast.success(`Order ${result.order.order_code} charged — GHS ${result.balance.toFixed(2)} left`);
    } catch (error) {
      toast.error(error.message || 'Could not charge wallet');
    } finally {
      setSubmitting(false);
    }
  };

  const markHandedOver = async () => {
    if (!lastOrder) return;
    setCompleting(true);
    try {
      await canteenService.updateOrderStatus(lastOrder.id, 'completed');
      toast.success('Order completed');
      reset();
    } catch (error) {
      toast.error(error.message || 'Could not complete order');
    } finally {
      setCompleting(false);
    }
  };

  if (!student) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: 380, margin: '0 auto' }}>
        <IdentificationCard size={40} weight="duotone" style={{ color: '#2563eb' }} />
        <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Scan the student's ID card to start a purchase.</div>
        <BarcodeScanner active={scanning} onScan={handleScan} manualLabel="Enter card number manually" />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 640, margin: '0 auto' }}>
      <div className="card" style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        {student.photo_url ? (
          <img src={student.photo_url} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <UserCircle size={56} weight="light" style={{ color: '#9ca3af' }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{student.first_name} {student.last_name}</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Balance: {formatCurrency(student.wallet.balance)}</div>
        </div>
        <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 13 }} onClick={reset}>
          <ArrowLeft size={14} /> Different card
        </button>
      </div>

      {student.wallet.frozen && (
        <div style={{ color: '#dc2626', fontSize: 13 }}><WarningCircle size={14} /> This wallet is frozen — charging will fail until a parent unfreezes it.</div>
      )}
      {student.wallet.low_balance && !student.wallet.frozen && (
        <div style={{ color: '#d97706', fontSize: 13 }}><WarningCircle size={14} /> Low balance.</div>
      )}

      {lastOrder ? (
        <div className="card" style={{ padding: 16, textAlign: 'center' }}>
          <CheckCircle size={32} weight="fill" style={{ color: '#16a34a' }} />
          <div style={{ fontWeight: 700, marginTop: 8 }}>{lastOrder.order_code} — {formatCurrency(lastOrder.total)}</div>
          {lastOrder.status === 'ready' ? (
            <>
              <div style={{ color: '#6b7280', fontSize: 13, margin: '6px 0 12px' }}>Charged — hand the order over now.</div>
              <button className="btn btn-primary" disabled={completing} onClick={markHandedOver}>
                {completing ? 'Completing...' : 'Mark Handed Over'}
              </button>
            </>
          ) : (
            <div style={{ color: '#6b7280', fontSize: 13, marginTop: 6 }}>Sent to the kitchen — track it in the Orders tab.</div>
          )}
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-secondary" onClick={reset}>Scan the next student</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 8 }}>
            {items.map((item) => {
              const blocked = blockedCategories.includes(item.item_type);
              const qty = cart[item.id] || 0;
              return (
                <div key={item.id} className="card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, opacity: blocked ? 0.5 : 1 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{item.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{titleCase(item.item_type)} · {formatCurrency(item.price)}{blocked ? ' · blocked for this student' : ''}</div>
                  </div>
                  <button className="btn btn-secondary" style={{ padding: '2px 8px' }} disabled={blocked || qty === 0} onClick={() => setQty(item.id, qty - 1)}><Minus size={12} /></button>
                  <span style={{ minWidth: 20, textAlign: 'center' }}>{qty}</span>
                  <button className="btn btn-secondary" style={{ padding: '2px 8px' }} disabled={blocked} onClick={() => setQty(item.id, qty + 1)}><Plus size={12} /></button>
                </div>
              );
            })}
            {!items.length && <div style={{ color: '#9ca3af', fontSize: 13 }}>No menu items yet.</div>}
          </div>

          <input
            className="form-control"
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <div className="card" style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Total</div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{formatCurrency(cartTotal)}</div>
            </div>
            <button
              className="btn btn-primary"
              disabled={!cartLines.length || submitting || student.wallet.frozen}
              onClick={charge}
            >
              {submitting ? 'Charging...' : 'Charge Wallet'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function CanteenAdminPage() {
  const { user } = useAuth();
  const canManageMenu = user && ['super_admin', 'school_admin'].includes(user.role);
  // canteen_staff previously had no tab bar at all (hardcoded into
  // OrdersTab) — everyone who can reach this page now also gets Scan &
  // Charge, since that's the counter-facing flow canteen_staff needs most.
  const [tab, setTab] = useState('scan');

  return (
    <Layout>
      <div className="page" style={{ display: 'grid', gap: 24 }}>
        <div className="page-header">
          <div>
            <h1>Canteen Management</h1>
            <p>{canManageMenu ? 'Manage the menu, charge students at the counter, and fulfill orders' : 'Charge students at the counter and fulfill orders'}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #e5e7eb' }}>
          {canManageMenu && (
            <button className={`tab-btn ${tab === 'menu' ? 'active' : ''}`} onClick={() => setTab('menu')}>
              <PencilSimple size={16} /> Menu
            </button>
          )}
          <button className={`tab-btn ${tab === 'scan' ? 'active' : ''}`} onClick={() => setTab('scan')}>
            <IdentificationCard size={16} /> Scan &amp; Charge
          </button>
          <button className={`tab-btn ${tab === 'orders' ? 'active' : ''}`} onClick={() => setTab('orders')}>
            <QrCode size={16} /> Orders
          </button>
        </div>

        {tab === 'menu' && canManageMenu ? <MenuTab /> : tab === 'scan' ? <ScanChargeTab /> : <OrdersTab />}
      </div>
    </Layout>
  );
}

export default CanteenAdminPage;
